/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import { LinkButton } from '@blend/enterprise-components';
import { State } from '../../../../redux';
import { NotaryAPI } from '../../../../api/notary/types';
import { notaryAPI } from '../../../../api/notary';
import { addNotification } from '../../../../actions/pageActions';
import { NotificationItem } from '../../../../components/common/Notifications/NotificationItem';
import { useRoom } from '../../../../hooks/useRoom';
import { ParticipantModal, ParticipantModalFormValues } from './ParticipantModal/ParticipantModal';
import { RemoveParticipantModal } from './RemoveParticipantModal/RemoveParticipantModal';
import { pick } from '@utils/lodash';
import { diagnostics } from '@utils/diagnostics';

export namespace ParticipantActions {
  export interface Props {
    participant: NotaryAPI.Participant;
  }
}

const styles = StyleSheet.create({
  actionsWrapper: {
    display: 'flex',
    width: '100%',
  },
  actionWrapper: {
    ':not(:last-child)': {
      paddingRight: 20,
    },
    ':last-child': {
      flexGrow: 1,
      textAlign: 'right',
    },
  },
});

export const ParticipantActions: React.FC<ParticipantActions.Props> = ({ participant }) => {
  const { roomId } = useRoom();
  const [editParticipantModalIsOpen, setEditParticipantModalIsOpen] = useState(false);
  const [removeParticipantModalIsOpen, setRemoveParticipantModalIsOpen] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const { roomDetail } = useSelector((state: State) => state);
  const dispatch = useDispatch();

  const genericParticipant = participant.sourceType === NotaryAPI.ParticipantSourceType.NOTARY_API;

  const handleEditSubmit = async (formValues: ParticipantModalFormValues) => {
    const updatedParticipantValues = pick(formValues, [
      'title',
      'firstName',
      'middleName',
      'lastName',
      'suffix',
      'requiresKba',
      'email',
      'phoneNumber',
      'signature',
      'rolesAndSignatures',
    ]);
    await notaryAPI.rooms.patchParticipant(roomId, participant.id, updatedParticipantValues);
  };

  const handleResendInvite = async () => {
    if (!participant.email) {
      return;
    }

    setIsSendingInvite(true);
    try {
      await notaryAPI.rooms.resendParticipantInvite(roomId, participant.id);

      // Show success notification
      const successNotification: NotificationItem.PropsWithoutId = {
        type: 'affirm',
        iconType: 'email',
        content: `Invite email sent to ${participant.firstName} ${participant.lastName}`,
        shouldAutoRemove: true,
      };
      addNotification(successNotification)(dispatch);
    } catch (err: any) {
      diagnostics.error(new Error('Failed to resend invite'), {
        err,
        participantId: participant.id,
        roomId,
      });

      // Show error notification
      const errorNotification: NotificationItem.PropsWithoutId = {
        type: 'error',
        iconType: 'error',
        content: 'Failed to send invite email. Please try again.',
        shouldAutoRemove: true,
      };
      addNotification(errorNotification)(dispatch);
    } finally {
      setIsSendingInvite(false);
    }
  };

  if (roomDetail.room!.isCallInProgress) {
    return null;
  }

  return (
    <>
      <div className={css(styles.actionsWrapper)}>
        <div className={css(styles.actionWrapper)}>
          <LinkButton
            label="Edit details"
            onClick={() => setEditParticipantModalIsOpen(true)}
            data-it={`link-edit-participant-${participant.id}`}
          />
        </div>
        {participant.email && (
          <div className={css(styles.actionWrapper)}>
            <LinkButton
              label={isSendingInvite ? 'Sending...' : 'Resend Invite'}
              onClick={handleResendInvite}
              disabled={isSendingInvite}
              data-it={`link-resend-invite-participant-${participant.id}`}
            />
          </div>
        )}
        {genericParticipant && (
          <div className={css(styles.actionWrapper)}>
            <LinkButton
              label="Remove"
              onClick={() => setRemoveParticipantModalIsOpen(true)}
              data-it={`link-remove-participant-${participant.id}`}
            />
          </div>
        )}
      </div>
      <ParticipantModal
        headerText="Edit Participant Details"
        subHeaderText={
          genericParticipant
            ? 'Blend Signing Room will send an email to this participant to reflect their edited information.'
            : 'This participant is listed as a borrower, so the details that can be edited for them are limited.'
        }
        roomId={roomId}
        isOpen={editParticipantModalIsOpen}
        handleFormSubmit={handleEditSubmit}
        handleClose={() => setEditParticipantModalIsOpen(false)}
        initialValue={participant}
        isGenericParticipant={genericParticipant}
      />
      <RemoveParticipantModal
        headerText="Remove participant"
        subHeaderText={`Are you sure you want to remove ${participant.firstName} ${participant.lastName} from this notarization?`}
        participantId={participant.id}
        roomId={roomId}
        isOpen={removeParticipantModalIsOpen}
        handleClose={() => setRemoveParticipantModalIsOpen(false)}
      />
    </>
  );
};
